/**
 * Admin login page.
 *
 * Calls POST /api/admin/auth/login → stores tokens → fetches /admin/auth/me
 * to hydrate the profile (including menu + node permissions) → redirects
 * to /platform/dashboard.
 */
import { Button, Card, Form, Input, message, Space } from 'antd';
import { LockOutlined, UserOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../api/client';
import { getAdminQuickLoginDefaults, isLoginQuickFillEnabled } from '../lib/dev-login';
import { useAuthStore } from '../store/auth';

interface LoginResponse {
  access_token: string;
  refresh_token: string;
}

interface AdminProfile {
  id: number;
  username: string;
  roles: { id: number; name: string; preset: string | null }[];
  is_super: boolean;
  menus: string[];
  nodes: string[];
}

export function Login() {
  const [form] = Form.useForm<{ username: string; password: string }>();
  const [loading, setLoading] = useState(false);
  const setTokens = useAuthStore((s) => s.setTokens);
  const setProfile = useAuthStore((s) => s.setProfile);
  const navigate = useNavigate();
  const quickFill = isLoginQuickFillEnabled();

  async function handleLogin(values: { username: string; password: string }) {
    setLoading(true);
    try {
      // Login is protected by @fastify/csrf-protection — must set `_csrf` cookie + header.
      const { token: csrfToken } = await api.get<{ token: string }>('/api/admin/auth/csrf-token');
      const tokens = await api.post<LoginResponse>('/api/admin/auth/login', values, {
        headers: { 'X-CSRF-Token': csrfToken },
      });
      setTokens(tokens.access_token, tokens.refresh_token);
      const profile = await api.get<AdminProfile>('/api/admin/auth/me');
      setProfile(profile);
      message.success(`欢迎，${profile.username}`);
      navigate('/platform/dashboard', { replace: true });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : '登录失败';
      message.error(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Card
        style={{ width: 360, boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}
        title={
          <div style={{ textAlign: 'center' }}>
            <img
              src="/orb.jpeg"
              alt=""
              width={120}
              height={40}
              style={{ objectFit: 'contain', marginBottom: 12, display: 'inline-block' }}
            />
            <div>ORB 运营后台</div>
          </div>
        }
      >
        <Form form={form} onFinish={handleLogin} layout="vertical" initialValues={{ username: '', password: '' }}>
          <Form.Item
            name="username"
            rules={[{ required: true, message: '请输入用户名' }]}
          >
            <Input prefix={<UserOutlined />} placeholder="用户名" size="large" />
          </Form.Item>
          <Form.Item
            name="password"
            rules={[{ required: true, message: '请输入密码' }]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="密码" size="large" />
          </Form.Item>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            {quickFill && (
              <Button
                block
                size="large"
                onClick={() => form.setFieldsValue(getAdminQuickLoginDefaults())}
              >
                填入演示管理员账号
              </Button>
            )}
            <Button type="primary" htmlType="submit" block size="large" loading={loading}>
              登录
            </Button>
          </Space>
        </Form>
      </Card>
    </div>
  );
}
